import React from "react";
import { TextField, type SxProps, type TextFieldProps } from "@mui/material";
import type { Theme } from "@mui/material/styles";

type RpTextFieldProps = Omit<TextFieldProps, "variant" | "size"> & {
  staticLabel?: boolean;
  size?: TextFieldProps["size"];
  variant?: TextFieldProps["variant"];
};

export default function RpTextField({
  value,
  onChange,
  onBlur,
  label = "",
  placeholder = "",
  error = false,
  helperText = "",
  staticLabel = false,
  disabled = false,
  type = "text",
  multiline = false,
  rows,
  size = "small",
  variant = "outlined",
  name,
  ...rest
}: RpTextFieldProps) {
  const focusSx = (theme: Theme) =>
    ({
      mt: "8px",
      ...(disabled &&
        !error && {
          backgroundColor: (theme.vars || theme).palette.action
            .disabledBackground,
        }),
      ...(disabled && {
        "& .MuiOutlinedInput-root": {
          color: (theme.vars || theme).palette.action.disabled,
        },
        "& .MuiOutlinedInput-input.Mui-disabled, & .MuiInputBase-input.Mui-disabled":
          {
            color: (theme.vars || theme).palette.action.disabled,
            WebkitTextFillColor: (theme.vars || theme).palette.action.disabled,
          },
      }),
      ...(rest.sx as object),
    }) as SxProps<Theme>;

  return (
    <TextField
      {...rest}
      name={name}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      label={label}
      placeholder={placeholder}
      type={type}
      disabled={disabled}
      multiline={multiline}
      rows={rows}
      size={size}
      variant={variant}
      fullWidth
      error={error}
      helperText={helperText}
      sx={focusSx as TextFieldProps["sx"]}
      slotProps={{
        inputLabel: staticLabel ? { shrink: true } : undefined,
        formHelperText: { sx: { minHeight: "1.25rem", mb: 0 } },
      }}
    />
  );
}
